/**
 * Select tool: click to pick, drag to move, shift-click to extend,
 * marquee-drag on empty canvas to box-select.
 *
 * Hit testing at Stage D is bbox-based — good enough for shapes and text,
 * slightly loose for freedraw (you can "grab" a scribble by clicking near
 * its bounding box even if not on an actual stroke). We can tighten this
 * per-type later if it becomes annoying.
 *
 * Drag-move is committed once on pointer-up: during the drag we hold a
 * delta in the tool and the canvas renders the selection offset by that
 * delta in the preview layer. This keeps a single undo entry per drag
 * and avoids hundreds of version bumps.
 */

import type { SceneElement } from "../../lib/scene";
import type { SceneStore } from "../store/sceneStore";
import { drawElement } from "../render/drawElement";
import { hitTestBBox, type Tool, type ToolPointerEvent } from "./tool";

type Mode = "idle" | "move" | "marquee";

export class SelectTool implements Tool {
  readonly kind = "select";

  private mode: Mode = "idle";
  private downX = 0;
  private downY = 0;
  private curX = 0;
  private curY = 0;
  /** Elements being moved, captured on down so undo has clean before-state. */
  private moving: SceneElement[] = [];
  /** Marquee-select needs to preserve the prior selection when shift is held. */
  private marqueeBaseSelection = new Set<string>();

  isActive(): boolean {
    return this.mode !== "idle";
  }

  onPointerDown(evt: ToolPointerEvent, store: SceneStore): void {
    if (evt.button !== 0) return;
    this.downX = this.curX = evt.x;
    this.downY = this.curY = evt.y;

    // Top-most element under cursor wins — iterate in reverse insertion order.
    const elements = store.listVisibleElements();
    let hit: SceneElement | undefined;
    for (let i = elements.length - 1; i >= 0; i--) {
      if (hitTestBBox(elements[i], evt.x, evt.y)) {
        hit = elements[i];
        break;
      }
    }

    if (hit) {
      const current = store.getState().selectedIds;
      if (evt.shift) {
        const next = new Set(current);
        if (next.has(hit.id)) next.delete(hit.id);
        else next.add(hit.id);
        store.setSelected(next);
      } else if (!current.has(hit.id)) {
        store.setSelected([hit.id]);
      }
      // Snapshot the elements being moved — one copy so undo "before" is stable.
      this.moving = [...store.getState().selectedIds]
        .map((id) => store.getElement(id))
        .filter((e): e is SceneElement => !!e && !e.isDeleted);
      this.mode = "move";
    } else {
      // Empty space → marquee.
      if (!evt.shift) store.setSelected([]);
      this.marqueeBaseSelection = new Set(store.getState().selectedIds);
      this.mode = "marquee";
    }
  }

  onPointerMove(evt: ToolPointerEvent, store: SceneStore): void {
    if (this.mode === "idle") return;
    this.curX = evt.x;
    this.curY = evt.y;

    if (this.mode === "marquee") {
      // Live update selection as the marquee grows.
      const minX = Math.min(this.downX, this.curX);
      const minY = Math.min(this.downY, this.curY);
      const maxX = Math.max(this.downX, this.curX);
      const maxY = Math.max(this.downY, this.curY);
      const next = new Set(this.marqueeBaseSelection);
      for (const el of store.listVisibleElements()) {
        if (
          el.x + el.width >= minX &&
          el.x <= maxX &&
          el.y + el.height >= minY &&
          el.y <= maxY
        ) {
          next.add(el.id);
        }
      }
      store.setSelected(next);
    }
  }

  onPointerUp(_evt: ToolPointerEvent, store: SceneStore): void {
    if (this.mode === "move" && this.moving.length > 0) {
      const dx = this.curX - this.downX;
      const dy = this.curY - this.downY;
      if (dx !== 0 || dy !== 0) {
        for (const original of this.moving) {
          const moved = store.bumpLocal({
            ...original,
            x: original.x + dx,
            y: original.y + dy,
          });
          store.upsertLocal(moved);
        }
      }
    }
    this.mode = "idle";
    this.moving = [];
  }

  cancel(): void {
    this.mode = "idle";
    this.moving = [];
    this.marqueeBaseSelection = new Set();
  }

  drawPreview(ctx: CanvasRenderingContext2D, _store: SceneStore): void {
    if (this.mode === "move" && this.moving.length > 0) {
      const dx = this.curX - this.downX;
      const dy = this.curY - this.downY;
      if (dx === 0 && dy === 0) return;
      ctx.save();
      ctx.translate(dx, dy);
      // Ghost: draw selected elements at 50% alpha so the original position
      // is still visible underneath as a reference point.
      ctx.globalAlpha = 0.5;
      for (const el of this.moving) drawElement(ctx, el);
      ctx.restore();
    } else if (this.mode === "marquee") {
      const minX = Math.min(this.downX, this.curX);
      const minY = Math.min(this.downY, this.curY);
      const w = Math.abs(this.curX - this.downX);
      const h = Math.abs(this.curY - this.downY);
      ctx.save();
      ctx.strokeStyle = "#6ac1c4";
      ctx.fillStyle = "rgba(106, 193, 196, 0.08)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.fillRect(minX, minY, w, h);
      ctx.strokeRect(minX, minY, w, h);
      ctx.restore();
    }
  }
}
